import React, { useCallback } from 'react';
import { apiService } from '../../services/api';
import { ApiKey } from '../../types';
import EntityFilter, { FILTER_PAGE_SIZE, FilterOption, FilterOptionPage } from './EntityFilter';
import { apiKeyLabel } from '../../utils/apiKey';

// Which keys a view may filter by. CLI views accept either CLI scope.
export const API_SCOPE = 'api:convert';
export const CLI_SCOPE = 'cli:analyze,cli:convert';

function apiKeyOption(key: ApiKey): FilterOption {
  return {
    id: key.id,
    label: apiKeyLabel(key),
    badge: key.is_active ? undefined : 'revoked',
  };
}

// API key filter for the current instance. Empty selection means every key.
function ApiKeyFilter({
  scope,
  value,
  onChange,
  className,
}: {
  scope: string;
  value: number[];
  onChange: (ids: number[]) => void;
  className?: string;
}) {
  const loadPage = useCallback(
    async (search: string, offset: number): Promise<FilterOptionPage> => {
      const res = await apiService.listApiKeys({
        scope,
        search: search || undefined,
        limit: FILTER_PAGE_SIZE,
        offset,
        sort: 'name',
        order: 'asc',
      });
      const keys: ApiKey[] = res.data.api_keys ?? [];
      return {
        options: keys.map(apiKeyOption),
        total: res.data.total ?? keys.length,
      };
    },
    [scope],
  );

  const loadSelected = useCallback(
    async (ids: number[]): Promise<FilterOption[]> => {
      const res = await apiService.listApiKeys({
        ids: ids.join(','),
        scope,
        limit: ids.length,
        offset: 0,
        sort: 'name',
        order: 'asc',
      });
      const keys: ApiKey[] = res.data.api_keys ?? [];
      return keys.map(apiKeyOption);
    },
    [scope],
  );

  return (
    <EntityFilter
      multiple
      ariaLabel="API key filter"
      allLabel="All API Keys"
      pluralNoun="API keys"
      value={value}
      onChange={onChange}
      loadPage={loadPage}
      loadSelected={loadSelected}
      resetKey={scope}
      className={className}
    />
  );
}

export default ApiKeyFilter;
